// src/components/learning/CourseNavBar.js
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Dialog from "../../dialogs";

/**
 * Props:
 * - prevItem: { route, title, kind } | null
 * - nextItem: { route, title, kind } | null
 * - isEndOfCourse: boolean
 * - nextCourseId?: string | number  (if you have it from API)
 * - onEndFallbackRoute?: string     (fallback when nextCourseId not provided)
 * - showProgress?: { current: number, total: number }
 */
const CourseNavBar = ({
  prevItem,
  nextItem,
  isEndOfCourse,
  nextCourseId,
  onEndFallbackRoute = "/learn", // change to your catalog or track page
  showProgress,
}) => {
  const navigate = useNavigate();
  const [examModal, setExamModal] = useState(null);

  // Scroll to top on mount (navigation)
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const EndButton = () => {
    if (!isEndOfCourse) return null;
    const to = nextCourseId
      ? `/learn/subcourse/${nextCourseId}`
      : onEndFallbackRoute;
    return (
      <button
        onClick={() => navigate(to)}
        className="inline-flex items-center justify-center rounded-lg bg-[#0F766E] text-white font-semibold px-4 py-2 hover:bg-[#0b5e58] transition-colors"
      >
        Next Course
      </button>
    );
  };

  return (
    <div className="mt-6 pt-4 flex flex-col md:flex-row items-center justify-between gap-3 p-4 bg-white rounded-xl">
      <Dialog
        open={!!examModal}
        onClose={() => setExamModal(null)}
        // footer={dialogFooter}
        className="border-none !ring-0"
      >
        <h3 className="text-xl font-bold leading-6 text-gray-800">
          Confirmation
        </h3>
        <p className="text-sm text-gray-700 mt-4 font-normal">
          Please note that you are not allowed to leave the exam page during the
          test. If you leave the page, you will be disqualified.
        </p>
        <div className="flex justify-end gap-3 mt-3">
          {" "}
          <div className="flex justify-end gap-3 mt-3">
            <button
              onClick={() => setExamModal(false)}
              className="px-4 py-2 text-sm font-medium text-[#B3261E] bg-white ring-1 ring-[#B3261E] rounded-lg hover:bg-red-50"
            >
              Next Time
            </button>
            <button
              onClick={() => navigate(examModal.route)}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-900 rounded-lg hover:bg-indigo-800"
            >
              Start Exam
            </button>
          </div>
        </div>
      </Dialog>

      {/* Prev / Next */}
      <div className="w-full md:w-auto flex items-center justify-end gap-2">
        {prevItem?.kind === "exam" ? (
          <button
            onClick={() => setExamModal(prevItem)}
            className={`px-4 py-2 rounded-lg !ring-1 text-sm font-semibold transition-colors !bg-transparent ${
              prevItem
                ? "!ring-gray-300 text-gray-700 hover:bg-gray-50"
                : "!ring-gray-200 text-gray-300 cursor-not-allowed"
            }`}
          >
            ← Prev
          </button>
        ) : (
          <Link
            to={
              prevItem?.kind === "exam"
                ? () => setExamModal(prevItem)
                : prevItem?.route || "#"
            }
            onClick={(e) => !prevItem && e.preventDefault()}
            className={`px-4 py-2 rounded-lg border text-sm font-semibold transition-colors ${
              prevItem
                ? "border-gray-300 text-gray-700 hover:bg-gray-50"
                : "border-gray-200 text-gray-300 cursor-not-allowed"
            }`}
          >
            ← Prev
          </Link>
        )}

        {!isEndOfCourse &&
          (nextItem?.kind === "exam" ? (
            <button
              onClick={() => setExamModal(nextItem)}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                nextItem
                  ? "bg-[#4F378A] text-white hover:bg-[#402e74]"
                  : "bg-gray-200 text-gray-400 cursor-not-allowed"
              }`}
            >
              Next →
            </button>
          ) : (
            <Link
              to={nextItem?.route || "#"}
              onClick={(e) => !nextItem && e.preventDefault()}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                nextItem
                  ? "bg-[#4F378A] text-white hover:bg-[#402e74]"
                  : "bg-gray-200 text-gray-400 cursor-not-allowed"
              }`}
            >
              Next →
            </Link>
          ))}

        {/* End-of-course CTA */}
        <EndButton />
      </div>

      {/* Mini labels */}
      <div className="w-full md:w-auto flex items-center justify-between md:justify-end gap-3 text-xs text-[#585F72]">
        <div className="truncate max-w-[14rem]">
          {prevItem ? (
            <>
              <span className="font-semibold">Prev:</span>{" "}
              <span className="opacity-80">
                {prevItem.kind === "exam" ? "Exam – " : ""}
                {prevItem.title}
              </span>
            </>
          ) : (
            <span className="opacity-50">At the beginning</span>
          )}
        </div>
        <div className="truncate max-w-[14rem] text-right">
          {isEndOfCourse ? (
            <span className="font-semibold">End of course</span>
          ) : nextItem ? (
            <>
              <span className="font-semibold">Next:</span>{" "}
              <span className="opacity-80">
                {nextItem.kind === "exam" ? "Exam – " : ""}
                {nextItem.title}
              </span>
            </>
          ) : (
            <span className="opacity-50">—</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default CourseNavBar;
